// v2 dry shrub — a genuinely twiggy scrap of desert scrub: a gnarled half-buried
// rootstock throws out wiry twigs that each fork midway into crooked tips (real
// branching, with one fork snapped and drooping dead), and only a few meagre
// dusty-olive leaf wisps cling near the surviving tips — the rest is bare dry
// wood. Variants re-deal the twig yaws, fork spreads and the wisp deal. Ported
// 1:1 from age-of-mcp's v2/dry-shrub.ts, using the library's shared `tropic`
// (h01, lerp, tube, tuft) helpers.
import * as THREE from "three";
import { defineModel, tropic, type Part, type Vec3 } from "../../lib";

const WOOD = "#9b7d4f";
const WOOD_DK = "#82663c";
const ROOT = "#6e5638";
const WISP = "#8e9560";
const WISP_2 = "#7c8551";

function dryShrubParts(v: number): Part[] {
  const seed = v * 31 + 9;
  const yaw = v * 2.1 + tropic.h01(seed, 0) * 0.9;
  const parts: Part[] = [
    // gnarled half-buried rootstock
    { geometry: new THREE.IcosahedronGeometry(0.065, 0), color: ROOT,
      position: [0, 0.025, 0], rotation: [0, yaw, 0],
      scale: [1.25, 0.5, 1.25] },
  ];
  // Five wiry twigs, each forking midway into two crooked tips.
  const tips: Vec3[] = [];
  for (let i = 0; i < 5; i++) {
    const a = yaw + (i / 5) * Math.PI * 2 + tropic.h01(seed, 10 + i) * 0.6;
    const mr = tropic.lerp(0.1, 0.16, tropic.h01(seed, 20 + i));
    const mid: Vec3 = [Math.cos(a) * mr,
      tropic.lerp(0.15, 0.22, tropic.h01(seed, 30 + i)), Math.sin(a) * mr];
    parts.push(tropic.tube([Math.cos(a) * 0.03, 0.03, Math.sin(a) * 0.03], mid,
      0.016, 0.01, i % 2 ? WOOD : WOOD_DK, 4));
    for (let f = 0; f < 2; f++) {
      const fa = a + (f ? 0.55 : -0.5) + tropic.h01(seed, 40 + i * 2 + f) * 0.4;
      const dead = i === 4 && f === 1;   // one snapped, drooping fork
      const tip: Vec3 = [
        mid[0] + Math.cos(fa) * 0.13,
        dead ? mid[1] - 0.07
             : mid[1] + tropic.lerp(0.1, 0.18, tropic.h01(seed, 60 + i * 2 + f)),
        mid[2] + Math.sin(fa) * 0.13,
      ];
      parts.push(tropic.tube(mid, tip, 0.008, 0.003, dead ? WOOD_DK : WOOD, 4));
      if (!dead) tips.push(tip);
    }
  }
  // A few meagre dusty leaf wisps clinging near the surviving tips.
  for (let i = 0; i < 3; i++) {
    const t = tips[(i * 3 + v) % tips.length]!;
    parts.push(tropic.tuft(t[0], t[1] + 0.02, t[2],
      tropic.lerp(0.05, 0.07, tropic.h01(seed, 80 + i)), 0.55,
      i % 2 ? WISP : WISP_2, yaw + i * 1.7));
  }
  return parts;
}

export const dryShrubV2 = defineModel({
  id: "dry-shrub-v2",
  batchId: "fable",
  name: "Dry shrub (v2)",
  tags: ["scenery","dry","shrub","low-poly","age-of-mcp","fable"],
  summary: "Low-poly dry shrub.",
  description: "Ported from the age-of-mcp gallery, v2 style.",
  author: "age-of-mcp",
  footprint: 0.3,
  variants: [
    { id: "variant-0", label: "Variant 1", parts: () => dryShrubParts(0) },
    { id: "variant-1", label: "Variant 2", parts: () => dryShrubParts(1) },
    { id: "variant-2", label: "Variant 3", parts: () => dryShrubParts(2) },
  ],
  animations: [],
});
